/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Divider,
  Grid,
  InputBase,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BuildIcon from "@mui/icons-material/Build";
import FeedIcon from "@mui/icons-material/Feed";
import HandymanIcon from "@mui/icons-material/Handyman";
import BusinessIcon from "@mui/icons-material/Business";
import { Vocabulary } from "../../Utils/Vocabulary";
import { handleChangeAutocomplete, setStatuses } from "../../Utils/Utils";
import useCustomMemo from "../GenericComponents/useCustomMemo";
import { useContext, useEffect, useState } from "react";
import { OrderContext } from "../../Contexts/OrderContext";
import styles from "../../Styles/order.module.css";
import { styled } from "@mui/system";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/UrlEnum";
import { PartnerModel, TechnicianModel } from "../../Utils/Models";

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  color: "black",
  fontWeight: "bold",
  backgroundColor: "white",
}));

const GroupItems = styled("ul")({
  padding: 0,
});

export default function GeneralInformation() {
  const orderContext = useContext(OrderContext);
  const phoneStyle = useMediaQuery("(max-width: 800px)");
  const cache = useCustomMemo();
  const statuses =
    setStatuses(
      cache.statuses || [],
      cache.workflows || [],
      (orderContext.order.workflow && orderContext.order.workflow?._id) || null,
    ) || [];
  const users = cache.users || [];
  const locations = cache.locations || [];
  const [partners, setPartners] = useState<PartnerModel[]>([]);
  const [partnerTechnicians, setPartnerTechnicians] = useState<
    TechnicianModel[]
  >([]);

  // Fetch partners on mount
  useEffect(() => {
    getData(`${urlEnum.customers}?isPartner=true`).then((res: any) => {
      if (res.data.status === 200) {
        setPartners(res.data.result);
      }
    });
  }, []);

  // Fetch technicians when partner changes
  useEffect(() => {
    if (orderContext.order.partner?._id) {
      getData(
        `${urlEnum.technicians}/partner/${orderContext.order.partner._id}`,
      ).then((res: any) => {
        if (res.data.status === 200) {
          setPartnerTechnicians(res.data.result);
        }
      });
    } else {
      setPartnerTechnicians([]);
    }
  }, [orderContext.order.partner]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <BookmarkAddedIcon
              fontSize="small"
              style={{
                marginRight: 10,
                verticalAlign: "middle",
              }}
            />
            {Vocabulary.status}
          </Typography>
          {orderContext.order.workflow ? (
            <Autocomplete
              size="small"
              id="status"
              freeSolo={false}
              disablePortal
              options={statuses}
              value={orderContext.order.status || null}
              getOptionLabel={(option: any) => option.name}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(event: any, value: any) => {
                const newOrderContext = structuredClone(orderContext.order);
                newOrderContext.oldStatus = orderContext.order.status;
                if (value) newOrderContext.status = value;
                else newOrderContext.status = null;
                orderContext.setOrder(newOrderContext);
              }}
              renderInput={(params: any) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )}
              style={{ minWidth: 250 }}
            />
          ) : (
            <Typography style={{ minWidth: 250 }}>
              {Vocabulary.chooseWorkflowFirst}
            </Typography>
          )}
        </div>
        <Divider />
      </Grid>
      {/* Partner */}
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <BusinessIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.partner}
          </Typography>
          <Autocomplete
            size="small"
            id="partner"
            freeSolo={false}
            disablePortal
            options={partners}
            value={orderContext.order.partner || null}
            getOptionLabel={(option: any) =>
              `${option.companyName || option.name}`
            }
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event: any, value: any) => {
              const newOrder = handleChangeAutocomplete(
                event,
                value,
                orderContext.order,
                "partner",
              );
              // Clear partnerTechnician when partner changes
              newOrder.partnerTechnician = null;
              orderContext.setOrder(newOrder);
            }}
            renderInput={(params: any) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            style={{ minWidth: 250 }}
          />
        </div>
        <Divider />
      </Grid>
      {/* Partner Technician */}
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <HandymanIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.technician}
          </Typography>
          {orderContext.order.partner ? (
            <Autocomplete
              size="small"
              id="partnerTechnician"
              freeSolo={false}
              disablePortal
              options={partnerTechnicians}
              value={orderContext.order.partnerTechnician || null}
              getOptionLabel={(option: any) =>
                `${option.name}${option.phone ? ` - ${option.phone}` : ""}`
              }
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(event: any, value: any) => {
                orderContext.setOrder(
                  handleChangeAutocomplete(
                    event,
                    value,
                    orderContext.order,
                    "partnerTechnician",
                  ),
                );
              }}
              noOptionsText={Vocabulary.noTechniciansForPartner}
              renderInput={(params: any) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              style={{ minWidth: 250 }}
            />
          ) : (
            <Typography style={{ minWidth: 250, color: "gray" }}>
              {Vocabulary.choosePartnerFirst}
            </Typography>
          )}
        </div>
        <Divider />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <EngineeringIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.handledBy}
          </Typography>
          <Autocomplete
            size="small"
            id="handledBy"
            freeSolo={false}
            disablePortal
            options={users}
            value={orderContext.order.handledBy || null}
            getOptionLabel={(option: any) => `${option.username}`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event: any, value: any) => {
              orderContext.setOrder(
                handleChangeAutocomplete(
                  event,
                  value,
                  orderContext.order,
                  "handledBy",
                ),
              );
            }}
            renderInput={(params: any) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            style={{ minWidth: 250 }}
          />
        </div>
        <Divider />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <BuildIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.serviceLocation}
          </Typography>
          <Autocomplete
            size="small"
            id="location"
            freeSolo={false}
            disablePortal
            options={locations}
            value={orderContext.order.location || null}
            getOptionLabel={(option: any) => option.name}
            isOptionEqualToValue={(option: any, value: any) =>
              option._id === value._id
            }
            onChange={(event: any, value: any) => {
              orderContext.setOrder(
                handleChangeAutocomplete(
                  event,
                  value,
                  orderContext.order,
                  "location",
                ),
              );
            }}
            renderInput={(params: any) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            style={{ minWidth: 250 }}
          />
        </div>
        <Divider />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={phoneStyle ? { marginBottom: 5 } : {}}>
            <LocationOnIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.internalLocation}
          </Typography>
        </div>
        <Divider />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <div
          className={
            phoneStyle
              ? styles.mainDivGeneralInfoMobile
              : styles.mainDivGeneralInfo
          }
        >
          <Typography style={{ flexShrink: 0, marginRight: 16, ...(phoneStyle ? { marginBottom: 5 } : {}) }}>
            <FeedIcon
              fontSize="small"
              style={{ marginRight: 10, verticalAlign: "middle" }}
            />
            {Vocabulary.tags}
          </Typography>
          <InputBase
            size="small"
            placeholder={Vocabulary.addTag}
            inputProps={{ style: { padding: "2px 0" } }}
            style={{ flex: 1, borderBottom: "1px solid #ccc", fontSize: 14 }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key !== "Enter") return;
              const val = (e.target as HTMLInputElement).value.trim();
              if (!val) return;
              const current = orderContext.order.tags || [];
              if (!current.includes(val)) {
                orderContext.setOrder({ ...orderContext.order, tags: [...current, val] });
              }
              (e.target as HTMLInputElement).value = "";
            }}
          />
        </div>
        {(orderContext.order.tags || []).length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {(orderContext.order.tags || []).map((tag: string, i: number) => (
              <span
                key={i}
                onClick={() => {
                  const updated = (orderContext.order.tags || []).filter((_: string, idx: number) => idx !== i);
                  orderContext.setOrder({ ...orderContext.order, tags: updated });
                }}
                title={Vocabulary.remove}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  backgroundColor: "#e3f2fd",
                  color: "#1565c0",
                  border: "1px solid #90caf9",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {tag}
                <span style={{ fontSize: 14, lineHeight: 1, opacity: 0.6 }}>×</span>
              </span>
            ))}
          </div>
        )}
        <Divider />
      </Grid>
    </Grid>
  );
}
